/**
 * useTerms
 * @description 공통 약관 뷰어
 * @returns {
 *   termsPopup: { ...termsPopup }, // 약관뷰어 popup Props
 *   openTerms,                     // 약관 뷰어 실행
 *   closeTerms,                    // 약관 닫기 실행
 *   onCloseTerms,
 *   getTermsInfos,
 *   showTermsInfo,
 * }
 * @author 951303
 * @history
 *   - [2025-10-02] 초기 작성
 */

import { useCallback, useRef, useState } from 'react';

import {
  Channel,
  Envs,
  Languages,
  Modules,
  NativeBridgeResponse,
  Pdf,
  useChannelContext,
  useConfig,
  useEnvContext,
  useLocale,
  usePdfViewerBridge,
} from 'scbk-services';

import { Dialog, usePopup } from 'scbk-shared-module';

import { TermsLangCode, TermsPdfViewerType, TermsType } from '../enums';
import type {
  HtmlExplainTerms,
  PdfProductTerms,
  PdfViewerTerms,
  ShowTermsInfo,
  TermsInfo,
  TermsParameter,
  UtlFncTrmInquiryProductTermsRequest,
  UtlFncTrmInquiryProductTermsResponse,
  UtlFncTrmInquiryTermsRequest,
  UtlFncTrmInquiryTermsResponse,
} from '../types';
import { useUtlFncTrmInquiryProductTerms, useUtlFncTrmInquiryTerms } from './api';

// 약관 Hook
export const useTerms = () => {
  const currentEnv = useEnvContext().getEnv(); // 환경변수
  const { channel } = useChannelContext();
  const { getLocale } = useLocale();

  const { doNPdfViewAndScroll, doNPdfViewer } = usePdfViewerBridge();
  const { getConfig } = useConfig();

  const { request: getTermsInfo } = useUtlFncTrmInquiryTerms({
    errorOnConfirm: () => {
      // router.back 방지
    },
  }); // MB 약관 조회 Hook
  const { request: getProductTermsInfo } = useUtlFncTrmInquiryProductTerms({
    errorOnConfirm: () => {
      // router.back 방지
    },
  }); // MB 상품약관 조회 Hook

  const termsPopup = usePopup('TermsComponent'); // 약관 팝업 Hook

  const [showTermsInfo, setShowTermsInfo] = useState<ShowTermsInfo | null>(null); // 조회약관정보
  const termsParameterRef = useRef<TermsParameter | null>(null); // 약관 뷰어 parameterRef

  // 약관 뷰어
  const openTerms = useCallback(async (termsParameter: TermsParameter) => {
    if (!termsParameter) {
      throw new Error('약관 뷰어 요청 데이터 오류!');
    }

    // 콜백셋팅
    termsParameterRef.current = termsParameter;
    termsPopup.setTrigger();

    switch (termsParameter.termsType) {
      case TermsType.HTML_VIEWER: // HTML_VIEWER 는 업무에서 넘겨준 html data set
        setShowTermsInfo({
          termsType: termsParameter.termsType,
          prvsnNm: termsParameter.prvsnNm ?? '', // 약관 제목(업무에서 설정값 우선적용)
          prvsnLongExpln: termsParameter.prvsnLongExpln ?? '',
          depthFlag: false,
          okBtnLabel: termsParameter.okBtnLabel,
          cancelBtnLabel: termsParameter.cancelBtnLabel,
        });

        termsPopup.open();
        break;

      case TermsType.HTML_EXPLAIN:
        // 약관 정보 조회
        const { termsInfoList: htmlTermsInfoList } = await getTermsInfos(termsParameter);
        const htmlTermsInfo = htmlTermsInfoList[0];

        if (htmlTermsInfo.prvsnLongExpln != null) {
          htmlTermsInfo.prvsnLongExpln = termsHelper.decodeHtml(htmlTermsInfo.prvsnLongExpln);
        }

        // html depth에 따른 해당 depth 영역만 추출
        const { handledPrvsnLongExpln, depthFlag } = termsHelper.handleHtmlDepth({
          htmlExplainTerms: termsParameter,
          htmlTermsInfo: htmlTermsInfo,
        });

        // 보여줄 약관정보 설정
        setShowTermsInfo({
          termsType: termsParameter.termsType,
          prvsnNm: termsParameter.prvsnNm ?? htmlTermsInfo.prvsnNm, // 약관 제목(업무에서 설정값 우선적용)
          prvsnLongExpln: handledPrvsnLongExpln,
          depthFlag: depthFlag,
          okBtnLabel: termsParameter.okBtnLabel,
          cancelBtnLabel: termsParameter.cancelBtnLabel,
        });

        // 약관 팝업 오픈
        termsPopup.open();
        break;

      case TermsType.PDF_VIEWER:
        // 기본값 설정
        termsParameter.pdfViwerType =
          channel != Channel.MB || currentEnv === Envs.LOCAL
            ? TermsPdfViewerType.EMBED // 인뱅/모웹 에서 EMBED 고정
            : (termsParameter.pdfViwerType ?? TermsPdfViewerType.NATIVE);

        // 약관 조회
        const { termsInfoList: pdfTermsInfoList } = await getTermsInfos(termsParameter);

        // PMS 도메인
        const mpmsPrdDomain = 'https://m.sc.co.kr';
        const mpmsDomain = getConfig(Modules.COMMON).mpmsDomain ?? mpmsPrdDomain;
        const resourceDir = '/mpms_RESOURCE';

        const pdfInfoList: Pdf.PdfInfo[] = pdfTermsInfoList.map((termsInfo) => ({
          title: termsInfo.prvsnNm ?? '',
          pdfUrl:
            TermsPdfViewerType.NATIVE || currentEnv === Envs.LOCAL
              ? `${mpmsDomain}${resourceDir}${termsInfo.attFileUrl}${termsInfo.attFileNm}`
              : `${mpmsPrdDomain}${resourceDir}${termsInfo.attFileUrl}${termsInfo.attFileNm}`, // TODO: 구글 PDF뷰어시 개발환경 접근 불가로 운영 도메인으로,
        }));

        console.log('useTerms > openTerms(PDF_VIEWER) - pdfInfoList : ', pdfInfoList);

        if (termsParameter.pdfViwerType === TermsPdfViewerType.NATIVE) {
          // 네이티브 PDF 뷰어
          if (termsParameter.pdfScrollFlag) {
            doNPdfViewAndScroll(
              {
                url: pdfInfoList,
              },
              (response) => {
                console.log(
                  'useTerms > openTerms(PDF_VIEWER) > doNPdfViewAndScroll - response : ',
                  response,
                );

                const pdfResult = termsHelper.parseNativePdfResponse(response);

                onCloseTerms(pdfResult.scrollDone !== 'N' ? true : undefined, pdfResult);
              },
            );
          } else {
            doNPdfViewer(
              {
                url: pdfInfoList,
              },
              (response) => {
                console.log(
                  'useTerms > openTerms(PDF_VIEWER) > doNPdfViewer - response : ',
                  response,
                );

                const pdfResult = termsHelper.parseNativePdfResponse(response);

                onCloseTerms(pdfResult.scrollDone !== 'N' ? true : undefined, pdfResult);
              },
            );
          }
        } else {
          // EMBED PDF VIEWER
          setShowTermsInfo({
            termsType: termsParameter.termsType,
            prvsnCd: '',
            prvsnNm: pdfTermsInfoList[0].prvsnNm ?? '',
            pdfInfoList: pdfInfoList,
            okBtnLabel: termsParameter.okBtnLabel,
            cancelBtnLabel: termsParameter.cancelBtnLabel,
          });
          termsPopup.open();
        }

        // if (channel == Channel.IB || channel == Channel.MW) {
        // 인뱅/모웹
        //   pdfInfoList.forEach((pdfInfo) => {
        //     window.open(pdfInfo.pdfUrl);
        //   });
        //   onCloseTerms(true);
        // }

        break;

      case TermsType.PDF_PRODUCT:
        // 기본값 설정
        termsParameter.pdfViwerType =
          channel != Channel.MB || currentEnv === Envs.LOCAL
            ? TermsPdfViewerType.EMBED // 인뱅/모웹 에서 EMBED 고정
            : (termsParameter.pdfViwerType ?? TermsPdfViewerType.NATIVE);

        console.log('useTerms > openTerms(PDF_PRODUCT) > termsParameter - ', termsParameter);

        const { productTermsInfo: productTermsInfo } = await getProductTermsInfos(termsParameter);

        // 홈페이지 도메인
        const homePagePrdDomain = 'https://www.standardchartered.co.kr';
        const homePageDomain = getConfig(Modules.COMMON).homePageDomain ?? homePagePrdDomain;
        const termsResourceDir = '/hp/file/ap/pd/';

        let prvsnNm = termsParameter.prdctPrvsnNm;
        if (!prvsnNm) {
          if (termsParameter.prdctViewType == '1') {
            prvsnNm = (productTermsInfo.prdctPrvsnNm ?? '') + ' 약관';
          } else if (termsParameter.prdctViewType == '4') {
            prvsnNm = (productTermsInfo.prdctPrvsnNm ?? '') + ' 상품설명서';
          }

          if (termsParameter.scrapFlag == 'Y') prvsnNm = '';
        }

        const viewPDFHangulNameChecker = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;

        if (
          !productTermsInfo.attFileNm ||
          viewPDFHangulNameChecker.test(productTermsInfo.attFileNm)
        ) {
          // 약관 PDF정보가 없거나 한글이 포함되었을경우 예비약관 호출
          openTerms({
            termsType: TermsType.HTML_EXPLAIN,
            prvsnNm: termsParameter.prdctPrvsnNm ?? prvsnNm,
            prvsnCd: termsParameter.reservedPrvsnCd,
            onClose: termsParameter.onClose,
          });
          return;
        }

        const pdfUrl =
          TermsPdfViewerType.NATIVE || currentEnv === Envs.LOCAL
            ? `${homePageDomain}${termsResourceDir}${productTermsInfo?.attFileNm}`
            : `${homePagePrdDomain}${termsResourceDir}${productTermsInfo?.attFileNm}`; // TODO: 구글 PDF뷰어시 개발환경 접근 불가로 운영 도메인으로

        if (termsParameter.pdfViwerType === TermsPdfViewerType.NATIVE) {
          // 네이티브 PDF 뷰어
          const pdfInfo: Pdf.PdfInfo = {
            title: prvsnNm ?? '',
            pdfUrl: pdfUrl,
          };

          if (termsParameter.pdfScrollFlag) {
            // NATIVE PDF VIEWER(SCROLL)
            doNPdfViewAndScroll(
              {
                url: [pdfInfo],
              },
              (response) => {
                console.log(
                  'useTerms > openTerms(PDF_PRODUCT) > doNPdfViewAndScroll - response : ',
                  response,
                );

                const pdfResult = termsHelper.parseNativePdfResponse(response);

                onCloseTerms(pdfResult.scrollDone !== 'N' ? true : undefined, pdfResult);
              },
            );
          } else {
            // NATIVE PDF VIEWER
            doNPdfViewer(
              {
                url: [pdfInfo],
              },
              (response) => {
                console.log(
                  'useTerms > openTerms(PDF_PRODUCT) > doNPdfViewer - response : ',
                  response,
                );

                const pdfResult = termsHelper.parseNativePdfResponse(response);

                onCloseTerms(pdfResult.scrollDone !== 'N' ? true : undefined, pdfResult);
              },
            );
          }
        } else {
          // EMBED PDF VIEWER
          setShowTermsInfo({
            termsType: termsParameter.termsType,
            prvsnCd: '',
            prvsnNm: prvsnNm,
            pdfUrl: pdfUrl,
            okBtnLabel: termsParameter.okBtnLabel,
            cancelBtnLabel: termsParameter.cancelBtnLabel,
          });
          termsPopup.open();
        }

        // if (channel == Channel.IB || channel == Channel.MW) {
        // 인터넷뱅킹 or 모바일웹
        //   await Promise.resolve();
        //   const windowPopup = window.open(pdfUrl);
        //   onCloseTerms(true);
        // }

        break;
      case TermsType.NO_TERMS:
        // 일부업무 약관없는 약관케이스 처리 즉시 callback 호출
        onCloseTerms(true);
        return;
      default:
        if (currentEnv === Envs.LOCAL || currentEnv === Envs.DEV) {
          Dialog.alert({ content: '약관 타입 오류' });
        }
        throw new Error('약관 타입 오류!');
    }
  }, []);

  // 약관 조회
  const getTermsInfos = useCallback(
    async (params: HtmlExplainTerms | PdfViewerTerms): Promise<UtlFncTrmInquiryTermsResponse> => {
      console.log('useTerms > getTermsInfos - params : ', params);

      // 약관조회 reqeust
      const request: UtlFncTrmInquiryTermsRequest = {
        actionType: params.termsType,
        loctnCdList:
          params.loctnCdList ??
          (getLocale() === Languages.EN
            ? [TermsLangCode.KOREAN, TermsLangCode.ENGLISH]
            : [TermsLangCode.KOREAN]),
        prvsnCdList:
          TermsType.HTML_EXPLAIN === params.termsType ? [params.prvsnCd] : params.prvsnCdList,
      };

      // 약관 조회
      const response = await getTermsInfo(request);

      if (!response) {
        Dialog.alert({ content: '등록된 약관이 없습니다. 약관정보를 확인해 주세요.' });
        throw new Error('등록된 약관이 없습니다. 약관정보를 확인해 주세요.');
      }

      return response;
    },
    [],
  );

  // 상품 약관 조회
  const getProductTermsInfos = useCallback(
    async (params: PdfProductTerms): Promise<UtlFncTrmInquiryProductTermsResponse> => {
      if (!params.prdctPrvsnId && !params.prdctPrvsnCd) {
        throw new Error('상품 약관 조회 아이디/코드 미존재!');
      }

      const request: UtlFncTrmInquiryProductTermsRequest = {
        scrapFlag: params.scrapFlag ?? 'N',
        ...params,
      };

      const response = await getProductTermsInfo(request);

      if (!response) {
        Dialog.alert({ content: '등록된 약관이 없습니다. 약관정보를 확인해 주세요.' });
        throw new Error('등록된 약관이 없습니다. 약관정보를 확인해 주세요.');
      }

      return response;
    },
    [],
  );

  /**
   * 약관 팝업 close callback
   */
  const onCloseTerms = useCallback((result?: boolean | undefined, nativeResult?: Pdf.PdfResult) => {
    const triggerCloseEvent = termsParameterRef.current?.triggerCloseEvent;
    const onClose = termsParameterRef.current?.onClose;

    // 약관 요청 데이터 초기화
    termsParameterRef.current = null;

    // 약관 팝업 닫기 처리
    termsPopup?.close();

    // 데이터 초기화
    setShowTermsInfo(null);

    // 2026.08.20 [TOBE 개선안 X버튼 클릭시에는 약관 동의 및 해제 처리 미실행(From. 김현수 부장님)]
    if (typeof result === 'undefined' && !triggerCloseEvent) {
      return;
    }

    // 업무 callback 실행
    onClose?.(result, nativeResult);
  }, []);

  // return useTerms
  return {
    termsPopup: { ...termsPopup },
    openTerms,
    onCloseTerms,
    getTermsInfos,
    showTermsInfo,
  };
};

const termsHelper = {
  // html depth값에 따라 해당 영역 추출
  handleHtmlDepth: ({
    htmlExplainTerms,
    htmlTermsInfo,
  }: {
    htmlExplainTerms: HtmlExplainTerms;
    htmlTermsInfo: TermsInfo;
  }): {
    handledPrvsnLongExpln: string;
    depthFlag: boolean;
  } => {
    const NODE_SEPARATOR = '.newTermsDepth';
    const styleRegex = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
    const depth: number = htmlExplainTerms.depth ?? 0;
    const agreeNumber: number = htmlExplainTerms.agreeNumber ?? 0;

    if (depth < 2 || agreeNumber === 0) {
      return {
        handledPrvsnLongExpln: htmlTermsInfo.prvsnLongExpln ?? '',
        depthFlag: false,
      };
    }

    const htmlStr: string = htmlTermsInfo.prvsnLongExpln ?? '';
    const styleStr: string = htmlStr.match(styleRegex)?.join('') ?? '';
    const bodyStr: string = htmlStr.replace(styleRegex, '');

    const doc = new DOMParser().parseFromString(bodyStr, 'text/html');
    const nodes = Array.from(doc.querySelectorAll(NODE_SEPARATOR));

    const idx = Math.max(0, agreeNumber - 1);
    const pickedHtmlDepth = nodes[idx];

    return {
      handledPrvsnLongExpln: pickedHtmlDepth ? styleStr + pickedHtmlDepth.outerHTML : htmlStr,
      depthFlag: true,
    };
  },
  // 네이티브 PDF 응답 파싱
  parseNativePdfResponse: (
    response: NativeBridgeResponse<Pdf.DoNPdfViewerResponse>,
  ): Pdf.PdfResult => {
    let pdfResult: Pdf.PdfResult = {
      title: '',
      pdfUrl: '',
      confirm: false,
      download: false,
      scrollDone: '',
    };

    if (!response || response?.status !== 'success') {
      Dialog.alert({ content: 'PDF로드중 오류가 발생하였습니다.' });
      throw new Error('PDF 로드 실패!');
    }

    const result = response.payload?.result;

    if (!result) {
      return pdfResult;
    }

    let pdfResultArray;
    if (typeof result === 'string') {
      // IOS
      try {
        pdfResultArray = JSON.parse(decodeURIComponent(result)) as Array<Pdf.PdfResult>;
      } catch (error) {
        console.warn('useTerms > termsHelper > parseNativePdfResult - error ', error);
      }
    } else {
      pdfResultArray = result;
    }

    if (Array.isArray(pdfResultArray) && pdfResultArray.length > 0) {
      pdfResult = pdfResultArray[0];
    }

    return pdfResult;
  },

  decodeHtml: (str: string) => {
    str = str.replaceAll(/&quot;/gi, '"');
    str = str.replaceAll(/&apos;/gi, "'");
    str = str.replaceAll(/&amp;/gi, '&');
    str = str.replaceAll(/&lt;/gi, '<');
    str = str.replaceAll(/&gt;/gi, '>');
    str = str.replaceAll(/&#xA;/gi, '<br>');
    str = str.replaceAll(/&#x0A;/gi, '<br>');
    str = str.replaceAll('\n', '');
    str = str.replaceAll('\t', '');

    return str;
  },
};
